/**
 * Detail candidats tab — searchable/filterable table + CSV/XLSX exports.
 * Extracted from AnalyticsCalls.jsx (iter29 refactor). Iso-behaviour.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { AiCommentaryBlock } from "./AiCommentaryBlock";
import { buildQueryParams, EmptyBlock, LoadingBlock } from "./shared";

export function DetailTab({ filters }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/analytics/calls/candidates-detail", {
            params: {
                ...buildQueryParams(filters),
                search: search || undefined,
                status: statusFilter || undefined,
                limit: 200,
            },
        })
            .then(({ data }) => alive && setData(data))
            .catch(() => alive && setData(null))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [filters, search, statusFilter]);

    async function exportFile(format) {
        try {
            const params = new URLSearchParams();
            const qp = buildQueryParams(filters);
            Object.entries(qp).forEach(([k, v]) => { if (v != null) params.append(k, v); });
            if (search) params.append("search", search);
            if (statusFilter) params.append("status", statusFilter);
            params.append("format", format);
            const resp = await api.get(`/analytics/calls/candidates-detail/export?${params.toString()}`, {
                responseType: "blob",
            });
            const blob = new Blob([resp.data], {
                type: format === "csv"
                    ? "text/csv;charset=utf-8"
                    : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `analytics-candidats-${new Date().toISOString().slice(0, 10)}.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Export ${format.toUpperCase()} téléchargé`);
        } catch (e) {
            toast.error("Export impossible");
        }
    }

    const rows = data?.items || [];
    return (
        <div className="space-y-4" data-testid="detail-tab">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un candidat, une offre, un téléphone…"
                        className="w-full pl-4 pr-4 py-2 rounded-full border-2 border-slate-100 focus:border-shariti-orange text-sm bg-white outline-none transition"
                        data-testid="detail-search"
                    />
                </div>
                <select
                    value={statusFilter || ""}
                    onChange={(e) => setStatusFilter(e.target.value || null)}
                    className="pl-3 pr-8 py-2 rounded-full border-2 border-slate-100 text-xs font-bold bg-white outline-none"
                    data-testid="detail-status-filter"
                >
                    <option value="">Tous statuts</option>
                    <option value="answered_complete">Répondu — complété</option>
                    <option value="answered_incomplete">Répondu — incomplet</option>
                    <option value="answered_unavailable">Répondu — indisponible</option>
                    <option value="answered_refusal">Répondu — refus</option>
                    <option value="voicemail">Messagerie</option>
                    <option value="no_answer">Pas de réponse</option>
                    <option value="busy">Occupé</option>
                    <option value="invalid_number">Numéro invalide</option>
                    <option value="technical_failure">Échec technique</option>
                </select>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => exportFile("csv")} data-testid="export-csv"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-slate-900 text-white text-[11px] font-black uppercase tracking-widest hover:bg-shariti-orange transition">
                        Export CSV
                    </button>
                    <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-emerald-500 transition">
                        Export Excel
                    </button>
                </div>
            </div>

            {loading ? <LoadingBlock label="Chargement du détail…" /> : (
                rows.length === 0 ? (
                    <EmptyBlock label="Aucun candidat sur les filtres actifs" />
                ) : (
                    <div className="rounded-2xl border-2 border-slate-100 bg-white overflow-x-auto" data-testid="detail-table">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    {["Candidat", "Offre", "Tentatives", "Dernier statut", "Score IA", "Verdict", "Statut PREREC", ""].map((h) => (
                                        <th key={h} className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.candidate_id} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/60" data-testid={`detail-row-${r.candidate_id}`}>
                                        <td className="px-3 py-2.5">
                                            <div className="text-sm font-bold text-shariti-black">{r.full_name}</div>
                                            <div className="text-[10px] text-slate-400">{r.phone || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600 truncate max-w-[220px]">{r.offer_title}</td>
                                        <td className="px-3 py-2.5 text-sm font-black text-shariti-orange">{r.attempts}</td>
                                        <td className="px-3 py-2.5 text-xs text-slate-600">{r.last_status_label}</td>
                                        <td className="px-3 py-2.5">
                                            {r.ai_score != null ? (
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black ${
                                                    r.ai_score >= 82 ? "bg-emerald-100 text-emerald-700" :
                                                    r.ai_score >= 65 ? "bg-amber-100 text-amber-700" :
                                                    "bg-slate-100 text-slate-500"
                                                }`}>{r.ai_score}/100</span>
                                            ) : <span className="text-slate-300 text-xs">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-slate-600">{r.ai_verdict || "—"}</td>
                                        <td className="px-3 py-2.5 text-xs font-medium text-slate-600 truncate">{r.prerec_status}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            <a href={r.profile_url}
                                                data-testid={`detail-open-${r.candidate_id}`}
                                                className="inline-block text-[11px] font-black uppercase tracking-widest text-shariti-orange hover:underline">
                                                Fiche →
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {data.total > rows.length && (
                            <div className="p-3 text-center text-xs text-slate-400">
                                {rows.length} sur {data.total} — utilisez l&apos;export pour l&apos;intégralité
                            </div>
                        )}
                    </div>
                )
            )}

            <AiCommentaryBlock scope="detail" filters={filters} testid="ai-commentary-detail" />
        </div>
    );
}
